import React, { useEffect, useState } from "react";
import { Button, useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import useFocusTrap from "../../hooks/useFocusTrap";
import { validateEmail } from "../auth/validationHelpers";
import api from "../login/interceptors";
import PanelistProfileFields from "./PanelistProfileFields";
import {
  emptyPanelistProfile,
  panelistErrorMessage,
  panelistFieldError,
  panelistIdOf,
  panelistNameError,
  panelistNameParts,
  panelistNamePayload,
  panelistProfilePayload,
  panelistsUrl,
  profileFromPanelist,
} from "../../utils/panelists";

const AddInterviewerModal = ({ isOpen, onClose, onCreated, interviewer }) => {
  const theme = useTheme();
  const modalRef = useFocusTrap(isOpen, onClose);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [experience, setExperience] = useState("");
  const [skills, setSkills] = useState([]);
  const [skillDraft, setSkillDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(panelistIdOf(interviewer));

  useEffect(() => {
    if (!isOpen) return;
    const profile = interviewer ? profileFromPanelist(interviewer) : emptyPanelistProfile();
    const names = panelistNameParts(interviewer);
    setFirstName(names.firstName);
    setLastName(names.lastName);
    setEmail(interviewer?.email || "");
    setRole(profile.role);
    setExperience(profile.experience);
    setSkills(profile.skills);
    setSkillDraft("");
    setError("");
  }, [isOpen, interviewer]);

  if (!isOpen) return null;

  const resetAndClose = () => {
    const profile = emptyPanelistProfile();
    setFirstName("");
    setLastName("");
    setEmail("");
    setRole(profile.role);
    setExperience(profile.experience);
    setSkills(profile.skills);
    setSkillDraft("");
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();
    const nameError = panelistNameError({ firstName: trimmedFirstName, lastName: trimmedLastName });
    if (nameError) {
      setError(nameError);
      return;
    }
    const emailError = validateEmail(email);
    if (emailError) {
      setError(emailError);
      return;
    }
    const fieldError = panelistFieldError({ role, experience, skills, skillDraft });
    if (fieldError) {
      setError(fieldError);
      return;
    }

    setSaving(true);
    setError("");
    try {
      const payload = {
        ...panelistNamePayload({ name: trimmedFirstName, lastName: trimmedLastName }),
        email: email.trim(),
        ...panelistProfilePayload({ role, experience, skills, skillDraft }),
      };
      if (isEdit) {
        await api.patch(`${panelistsUrl}/${panelistIdOf(interviewer)}`, payload);
      } else {
        await api.post(panelistsUrl, payload);
      }
      const profile = emptyPanelistProfile();
      setFirstName("");
      setLastName("");
      setEmail("");
      setRole(profile.role);
      setExperience(profile.experience);
      setSkills(profile.skills);
      setSkillDraft("");
      onCreated();
      onClose();
    } catch (err) {
      setError(panelistErrorMessage(err, isEdit ? "Could not update panelist." : "Could not add panelist."));
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    borderColor: theme.palette.secondary.v4,
    color: theme.palette.text.main,
  };

  return (
    <div className="fixed inset-0 flex items-start justify-center z-50 bg-black bg-opacity-70">
      <div className="w-full flex justify-center items-start mt-[70px]">
        <div
          ref={modalRef}
          className="modal-content relative w-full max-w-47rem bg-white rounded-lg shadow-xl max-h-[80vh] flex flex-col overflow-hidden"
          role="dialog"
          aria-modal="true"
          aria-labelledby="add-interviewer-title"
        >
          <div
            className="modal-header flex justify-between items-center py-4 px-5 border-b"
            style={{
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.secondary.v4,
            }}
          >
            <h2 id="add-interviewer-title" className="font-semibold">
              {isEdit ? "Edit Panelist" : "Add Panelist"}
            </h2>
            <button
              onClick={resetAndClose}
              aria-label="Close modal"
              className="inline-flex items-center justify-center rounded-full bg-black text-white h-6 w-6 hover:bg-gray-800 transition-colors"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          <div className="flex flex-col px-5 sm:px-6 py-6 gap-5 overflow-y-auto">
            {!isEdit && (
              <p className="text-sm text-accent-gray-light">
                They'll get an email invite to join as a Panelist. The link lasts 7 days.
              </p>
            )}
            <div className="flex gap-3">
              <div className="flex-1">
                <label
                  htmlFor="interviewer-first-name"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text.main }}
                >
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  id="interviewer-first-name"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={200}
                  placeholder="Enter first name"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                  autoFocus
                />
              </div>
              <div className="flex-1">
                <label
                  htmlFor="interviewer-last-name"
                  className="block text-sm font-medium mb-2"
                  style={{ color: theme.palette.text.main }}
                >
                  Last name
                </label>
                <input
                  id="interviewer-last-name"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={200}
                  placeholder="Enter last name"
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={inputStyle}
                />
              </div>
            </div>
            <div>
              <label
                htmlFor="interviewer-email"
                className="block text-sm font-medium mb-2"
                style={{ color: theme.palette.text.main }}
              >
                Email <span className="text-red-500">*</span>
              </label>
              <input
                id="interviewer-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={inputStyle}
              />
            </div>
            <PanelistProfileFields
              idPrefix="interviewer"
              role={role}
              onRoleChange={setRole}
              experience={experience}
              onExperienceChange={setExperience}
              skills={skills}
              onSkillsChange={setSkills}
              skillDraft={skillDraft}
              onSkillDraftChange={setSkillDraft}
              inputClassName="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              inputStyle={inputStyle}
            />
            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}
          </div>

          <div
            className="w-full py-3 px-5 flex justify-end border-t"
            style={{ borderColor: theme.palette.secondary.v4 }}
          >
            <Button
              onClick={handleSubmit}
              disabled={saving}
              sx={{
                backgroundColor: theme.palette.info.main,
                textTransform: "none",
                color: theme.palette.text.light,
              }}
            >
              {saving ? "Saving..." : isEdit ? "Save" : "Add Panelist"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddInterviewerModal;
